import React, { useState } from 'react';
import { WifiOff, Clock, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useOffline } from '../hooks/useOffline';

/**
 * OfflineBadge
 * Floating badge shown when the user loses internet connectivity.
 * Displays a count of queued actions and lets the user expand to see them.
 *
 * Props:
 *  - flushFn: async (action) => void — called per queued action when back online
 *  - className: extra Tailwind classes for positioning override
 */
export default function OfflineBadge({ flushFn, className = '' }) {
  const { isOffline, isOnline, queue, queueCount, dequeueAction } = useOffline(flushFn);
  const [expanded, setExpanded] = useState(false);

  // Hide badge when online and no queued items
  if (isOnline && queueCount === 0) return null;

  const actionLabel = (type) => {
    switch (type) {
      case 'send_message': return 'Message';
      case 'send_offer': return 'Offer';
      case 'save_draft': return 'Draft';
      default: return type ?? 'Action';
    }
  };

  return (
    <div
      className={`fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-1 ${className}`}
      role="status"
      aria-live="polite"
    >
      {/* Main badge */}
      <button
        onClick={() => queueCount > 0 && setExpanded(v => !v)}
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium transition-all
          ${isOffline
            ? 'bg-red-600 text-white' :'bg-amber-500 text-white'
          }
          ${queueCount > 0 ? 'cursor-pointer hover:opacity-90' : 'cursor-default'}
        `}
      >
        <WifiOff size={15} className="shrink-0" />
        <span>
          {isOffline ? "You're offline" : 'Back online'}
          {queueCount > 0 && (
            <span className="ml-1.5 bg-white/25 rounded-full px-1.5 py-0.5 text-xs">
              {queueCount} queued
            </span>
          )}
        </span>
        {queueCount > 0 && (
          expanded
            ? <ChevronUp size={14} className="shrink-0" />
            : <ChevronDown size={14} className="shrink-0" />
        )}
      </button>

      {/* Queued actions panel */}
      {expanded && queueCount > 0 && (
        <div className="w-72 bg-card border border-border rounded-xl shadow-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-border flex items-center gap-2">
            <Clock size={13} className="text-muted-foreground" />
            <span className="text-xs font-semibold text-foreground">Queued — will send when online</span>
          </div>
          <ul className="max-h-48 overflow-y-auto divide-y divide-border">
            {queue?.map(action => (
              <li key={action?.id} className="flex items-center justify-between px-3 py-2 text-xs text-foreground">
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="font-medium truncate">{actionLabel(action?.type)}</span>
                  <span className="text-muted-foreground truncate">
                    {action?.payload?.preview || action?.payload?.title || action?.payload?.body?.slice?.(0, 40) || '—'}
                  </span>
                </div>
                <button
                  onClick={() => dequeueAction(action?.id)}
                  className="ml-2 shrink-0 p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Remove from queue"
                >
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
